'use client';

import React, { useContext, useEffect, useMemo } from 'react';
import { MessageSquareText } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@components/ui/shadcn';
import { Button } from '@components/components/ui/button';
import { Textarea } from '@components/components/ui/textarea';
import { Input } from '@components/components/ui/input';
import { ClientAuthContext } from '@/auth/ClientAuthProvider';
import { useCodeGenerationStore } from '@/stores/useCodeGenerationStore';
import { useFeedback } from '@/hooks/useFeedback';
import { explorerAnalytics } from '@/lib/analytics';
import { RATINGS, FeedbackRating } from '@/utils/feedback';
import RatingButton from '@/components/feedback/RatingButton';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

export const ExplorerFeedbackForm: React.FC = () => {
  const { user, isLoggedIn } = useContext(ClientAuthContext);
  const { currentSessionId, messages, isTypingFollowUp } =
    useCodeGenerationStore();
  const isAiFeedback = !!currentSessionId;

  const {
    rating,
    isModalOpen,
    isSubmitting,
    email,
    comment,
    commentErrors,
    setRating,
    setIsModalOpen,
    setEmail,
    setComment,
    setCommentErrors,
    submitAiFeedback,
    submitGeneralFeedback,
    closeFeedback,
    resetFeedback,
  } = useFeedback();

  const MIN_CHARS = 3;
  const MAX_CHARS = 1000;

  const shouldShowFeedbackButton = useMemo(() => {
    const hasCompletedAssistantMessage = messages.some(
      (message) =>
        message.role === 'assistant' && message.content.trim().length > 0
    );

    if (messages.length === 0) {
      return !isAiFeedback;
    }

    return hasCompletedAssistantMessage && !isTypingFollowUp;
  }, [isAiFeedback, messages, isTypingFollowUp]);

  const availableRatings = RATINGS;

  const feedbackButtonVariants = {
    hidden: {
      opacity: 0,
      transition: {
        duration: 0.4,
        ease: [0.4, 0.0, 0.2, 1],
      },
    },
    show: {
      opacity: 1,
      transition: {
        duration: 0.5,
        delay: 1.3,
        ease: [0.4, 0.0, 0.2, 1],
      },
    },
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024 && isModalOpen) {
        setIsModalOpen(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isModalOpen, setIsModalOpen]);

  useEffect(() => {
    if (isModalOpen) {
      if (isLoggedIn && user?.email) {
        setEmail(user.email);
      } else if (!isLoggedIn) {
        setEmail('');
      }
      setCommentErrors([]);
    }
  }, [isModalOpen, isLoggedIn, user?.email, setEmail]);

  const handleModalOpen = () => {
    if (window.innerWidth >= 1024) {
      setIsModalOpen(true);
      explorerAnalytics.feedbackOpened({
        context: isAiFeedback ? 'ai' : 'general',
        isLoggedIn: !!isLoggedIn,
      });
      if (!isAiFeedback && user?.email) {
        setEmail(user.email);
      }
    }
  };

  const handleModalClose = (open: boolean) => {
    setIsModalOpen(open);
    if (!open) {
      closeFeedback(true);
      setCommentErrors([]);
    }
  };

  const handleRatingClick = (value: FeedbackRating) => {
    setRating(value);
    explorerAnalytics.feedbackRatingSelected({
      rating: value === FeedbackRating.POSITIVE ? 'positive' : 'negative',
      context: isAiFeedback ? 'ai' : 'general',
    });
  };

  const handleSubmit = async () => {
    const errors: string[] = [];

    // Validate rating
    if (rating === null) {
      errors.push('Please select a rating');
    }

    // Validate email (only for non-logged-in users)
    const emailToUse = isLoggedIn && user?.email ? user.email : email;
    if (!isLoggedIn && emailToUse.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailToUse.trim())) {
        errors.push('Please enter a valid email address');
      }
    }

    // Validate comment
    const trimmedComment = comment.trim();
    if (trimmedComment.length > 0) {
      if (trimmedComment.length < MIN_CHARS) {
        errors.push(`Please enter at least ${MIN_CHARS} characters`);
      }
      if (trimmedComment.length > MAX_CHARS) {
        errors.push(`Comment must be less than ${MAX_CHARS} characters`);
      }
    }

    if (errors.length > 0) {
      setCommentErrors(errors);
      return;
    }

    setCommentErrors([]);

    try {
      if (isAiFeedback) {
        await submitAiFeedback(
          rating!,
          emailToUse.trim(),
          trimmedComment || undefined
        );
      } else {
        await submitGeneralFeedback(rating!, emailToUse.trim(), trimmedComment);
      }

      explorerAnalytics.feedbackSubmitted({
        rating: rating === FeedbackRating.POSITIVE ? 'positive' : 'negative',
        hasComment: trimmedComment.length > 0,
        commentLength: trimmedComment.length,
        context: isAiFeedback ? 'ai' : 'general',
        isLoggedIn: !!isLoggedIn,
      });

      toast.success('Thank you for your feedback!');
      setIsModalOpen(false);
      resetFeedback();
      setCommentErrors([]);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Error submitting feedback! Please try again.';
      toast.error(errorMessage);
    }
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  };

  const handleCommentChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setComment(event.target.value);
  };

  const feedbackButton = (
    <Button
      onClick={handleModalOpen}
      aria-label="Give Feedback"
      className="py-4 px-4 rounded-xl shadow-lg hover:scale-105 transition-all bg-shade-teal-500 hover:bg-shade-teal-500 text-white font-medium flex items-center gap-2 border border-neutral-100"
    >
      <MessageSquareText className="h-4 w-4" />
      <span>Give Feedback</span>
    </Button>
  );

  if (!shouldShowFeedbackButton) {
    return null;
  }

  return (
    <>
      {messages.length > 0 ? (
        <motion.div
          key="feedback-button"
          initial="hidden"
          animate={shouldShowFeedbackButton ? 'show' : 'hidden'}
          variants={feedbackButtonVariants}
          style={{
            pointerEvents: shouldShowFeedbackButton ? 'auto' : 'none',
          }}
          className="fixed bottom-4 right-4 z-50"
        >
          {feedbackButton}
        </motion.div>
      ) : (
        <div className="fixed bottom-4 right-4 z-50">{feedbackButton}</div>
      )}

      <Dialog open={isModalOpen} onOpenChange={handleModalClose}>
        <DialogContent
          className={cn(
            'w-[380px] max-w-[90%] bg-white rounded-xl p-6 border border-neutral-200'
          )}
        >
          <DialogHeader className="text-center flex flex-col gap-1">
            <DialogTitle className="text-center text-xl font-semibold text-[#1A1D21] mt-6">
              We value your opinion!
            </DialogTitle>

            <DialogDescription className="text-center text-sm text-[#1A1D21]">
              How would you rate your overall experience?
            </DialogDescription>
          </DialogHeader>

          {/* Rating Buttons */}
          <div className="flex justify-center gap-4 mb-4">
            {availableRatings.map((ratingObject) => (
              <RatingButton
                key={ratingObject.id}
                rating={ratingObject}
                currentRating={rating}
                onRatingClick={handleRatingClick}
                disabled={isSubmitting}
              />
            ))}
          </div>

          {/* Email field (only logged-out users) */}
          {!isLoggedIn && (
            <div className="mb-4 text-left">
              <label className="block text-sm mb-1">Your email:</label>
              <Input
                type="email"
                value={email}
                placeholder="you@example.com"
                onChange={handleEmailChange}
                className="w-full h-10 px-3 border border-neutral-300 rounded-lg text-sm"
                data-pendo-mask
              />
            </div>
          )}

          {/* Comment Box */}
          <div className="mb-4">
            <label className="block text-sm mb-1">
              Anything you’d like to share with us?
            </label>
            <Textarea
              value={comment}
              onChange={handleCommentChange}
              className="w-full min-h-[100px] resize-none text-sm border border-neutral-300 rounded-lg"
              data-pendo-mask
              maxLength={MAX_CHARS}
            />
            {commentErrors.length > 0 && (
              <div className="mt-2">
                {commentErrors.map((error, index) => (
                  <p key={index} className="text-xs text-red-500">
                    {error}
                  </p>
                ))}
              </div>
            )}
          </div>

          <DialogFooter className="mt-2 flex flex-col sm:flex-col gap-4 sm:space-x-0">
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="w-full h-10 bg-[#0472E1] hover:bg-[#0472E1]/90 text-white rounded-lg text-sm disabled:opacity-50"
            >
              {isSubmitting ? 'Submitting…' : 'Submit Feedback'}
            </Button>
            <p className="text-xs text-gray-600 text-center m-0">
              This site is protected by reCAPTCHA and the Google{' '}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline hover:text-blue-700"
              >
                Privacy Policy
              </a>{' '}
              and{' '}
              <a
                href="https://policies.google.com/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline hover:text-blue-700"
              >
                Terms of Service
              </a>{' '}
              apply.
            </p>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ExplorerFeedbackForm;
